import { lazy } from "react";
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  type RouteObject,
} from "react-router-dom";

import { SessionProvider } from "../features/auth/session/session-provider";
import { MemberGuard } from "../features/auth/session/member-guard";
import { AdminGuard } from "../features/admin/auth/admin-guard";
import { AdminRouteGuard } from "../features/admin/auth/admin-route-guard";
import { AppRouteError } from "./app-route-error";
import { RouteBoundary } from "./route-boundary";

const StorefrontLayout = lazy(async () => {
  const { StorefrontLayout: Layout } = await import(
    "../layouts/storefront/storefront-layout"
  );

  return { default: Layout };
});

const HomePage = lazy(async () => {
  const { HomePage: Page } = await import(
    "../features/storefront/home/home-page"
  );

  return { default: Page };
});

const AboutPage = lazy(async () => {
  const { AboutPage: Page } = await import("../features/storefront/about/about-page");
  return { default: Page };
});

const ClubPage = lazy(async () => {
  const { ClubPage: Page } = await import("../features/storefront/club/club-page");
  return { default: Page };
});

const CatalogPage = lazy(async () => {
  const { CatalogPage: Page } = await import(
    "../features/catalog/catalog-page"
  );

  return { default: Page };
});

const ProductPage = lazy(async () => {
  const { ProductPage: Page } = await import(
    "../features/product/product-page"
  );

  return { default: Page };
});

const CheckoutLayout = lazy(async () => {
  const { CheckoutLayout: Layout } = await import(
    "../layouts/checkout/checkout-layout"
  );

  return { default: Layout };
});

const CheckoutPage = lazy(async () => {
  const { CheckoutPage: Page } = await import(
    "../features/checkout/checkout-page"
  );

  return { default: Page };
});

const ConfirmationPage = lazy(async () => {
  const { ConfirmationPage: Page } = await import(
    "../features/checkout/confirmation/confirmation-page"
  );

  return { default: Page };
});

const UpsellPage = lazy(async () => {
  const { UpsellPage: Page } = await import(
    "../features/checkout/upsell/upsell-page"
  );

  return { default: Page };
});

const AuthLayout = lazy(async () => {
  const { AuthLayout: Layout } = await import("../layouts/auth/auth-layout");

  return { default: Layout };
});

const LoginPage = lazy(async () => {
  const { LoginPage: Page } = await import(
    "../features/auth/login/login-page"
  );

  return { default: Page };
});

const FirstAccessPage = lazy(async () => {
  const { FirstAccessPage: Page } = await import(
    "../features/auth/password/first-access-page"
  );

  return { default: Page };
});

const RecoveryPage = lazy(async () => {
  const { RecoveryPage: Page } = await import(
    "../features/auth/password/recovery-page"
  );

  return { default: Page };
});

const ResetPage = lazy(async () => {
  const { ResetPage: Page } = await import(
    "../features/auth/password/reset-page"
  );

  return { default: Page };
});

const MembersLayout = lazy(async () => {
  const { MembersLayout: Layout } = await import(
    "../layouts/members/members-layout"
  );

  return { default: Layout };
});

const OverviewPage = lazy(async () => {
  const { OverviewPage: Page } = await import(
    "../features/members/overview/overview-page"
  );

  return { default: Page };
});

const LibraryPage = lazy(async () => {
  const { LibraryPage: Page } = await import(
    "../features/members/library/library-page"
  );

  return { default: Page };
});

const PurchasedProductPage = lazy(async () => {
  const { PurchasedProductPage: Page } = await import(
    "../features/members/product/purchased-product-page"
  );

  return { default: Page };
});

const OrdersPage = lazy(async () => {
  const { OrdersPage: Page } = await import(
    "../features/members/orders/orders-page"
  );

  return { default: Page };
});

const OrderDetailPage = lazy(async () => {
  const { OrderDetailPage: Page } = await import(
    "../features/members/orders/order-detail-page"
  );

  return { default: Page };
});

const ClubMemberPage = lazy(async () => {
  const { ClubMemberPage: Page } = await import("../features/members/club/club-page");
  return { default: Page };
});

const SubscriptionsPage = lazy(async () => {
  const { SubscriptionsPage: Page } = await import(
    "../features/members/subscriptions/subscriptions-page"
  );

  return { default: Page };
});

const PaymentHistoryPage = lazy(async () => {
  const { PaymentHistoryPage: Page } = await import(
    "../features/members/history/history-pages"
  );
  return { default: Page };
});

const DownloadHistoryPage = lazy(async () => {
  const { DownloadHistoryPage: Page } = await import(
    "../features/members/history/history-pages"
  );
  return { default: Page };
});

const AccountPage = lazy(async () => {
  const { AccountPage: Page } = await import(
    "../features/members/account/account-page"
  );

  return { default: Page };
});

const AdminShell = lazy(async () => {
  const { AdminShell: Layout } = await import("../layouts/admin/admin-shell");
  return { default: Layout };
});

const AdminDashboardPage = lazy(async () => {
  const { DashboardPage: Page } = await import(
    "../features/admin/dashboard/dashboard-page"
  );
  return { default: Page };
});

const AdminAnalyticsPage = lazy(async () => {
  const { AnalyticsPage: Page } = await import("../features/admin/analytics/analytics-page");
  return { default: Page };
});

const AdminAnalyticsConsentPage = lazy(async () => {
  const { AnalyticsConsentPage: Page } = await import("../features/admin/analytics/analytics-consent-page");
  return { default: Page };
});

const AdminCatalogListPage = lazy(async () => {
  const { CatalogListPage: Page } = await import(
    "../features/admin/catalog/catalog-list-page"
  );
  return { default: Page };
});

const AdminProductEditPage = lazy(async () => {
  const { ProductEditPage: Page } = await import(
    "../features/admin/catalog/product-edit-page"
  );
  return { default: Page };
});

const AdminProductPreviewPage = lazy(async () => {
  const { ProductPreviewPage: Page } = await import(
    "../features/admin/catalog/product-preview-page"
  );
  return { default: Page };
});

const AdminCategoryPage = lazy(async () => {
  const { CategoryPage: Page } = await import(
    "../features/admin/catalog/category-page"
  );
  return { default: Page };
});

const AdminAuditPage = lazy(async () => {
  const { AdminAuditPage: Page } = await import("../features/admin/audit-page");
  return { default: Page };
});

const AdminOrdersPage = lazy(async () => {
  const { AdminOrdersPage: Page } = await import("../features/admin/orders/orders-list-page");
  return { default: Page };
});

const AdminOrderDetailPage = lazy(async () => {
  const { AdminOrderDetailPage: Page } = await import("../features/admin/orders/order-detail-page");
  return { default: Page };
});

const AdminCustomersPage = lazy(async () => {
  const { AdminCustomersPage: Page } = await import("../features/admin/customers/customers-list-page");
  return { default: Page };
});

const AdminCustomerDetailPage = lazy(async () => {
  const { AdminCustomerDetailPage: Page } = await import("../features/admin/customers/customer-detail-page");
  return { default: Page };
});

const AdminSubscriptionsPage = lazy(async () => {
  const { AdminSubscriptionsPage: Page } = await import("../features/admin/subscriptions/subscriptions-list-page");
  return { default: Page };
});

const AdminSubscriptionDetailPage = lazy(async () => {
  const { AdminSubscriptionDetailPage: Page } = await import("../features/admin/subscriptions/subscription-detail-page");
  return { default: Page };
});

const AdminCouponsPage = lazy(async () => {
  const { AdminCouponsPage: Page } = await import("../features/admin/offers-pages");
  return { default: Page };
});

const AdminOrderBumpsPage = lazy(async () => {
  const { AdminOrderBumpsPage: Page } = await import("../features/admin/offers-pages");
  return { default: Page };
});

const AdminUpsellsPage = lazy(async () => {
  const { UpsellsPage: Page } = await import("../features/admin/upsells/upsells-page");
  return { default: Page };
});

const AdminPreSellsPage = lazy(async () => {
  const { PreSellsPage: Page } = await import(
    "../features/admin/pre-sells/pre-sells-page"
  );
  return { default: Page };
});

const AdminCrossSellsPage = lazy(async () => {
  const { CrossSellsPage: Page } = await import("../features/admin/cross-sells/cross-sells-page");
  return { default: Page };
});

const CheckoutStudioPage = lazy(async () => {
  const { CheckoutStudioPage: Page } = await import(
    "../features/admin/checkout-studio/checkout-studio-page"
  );
  return { default: Page };
});

const HomeStudioPage = lazy(async () => {
  const { HomeStudioPage: Page } = await import("../features/admin/home-studio/home-studio-page");
  return { default: Page };
});

const AdminEmailsPage = lazy(async () => {
  const { EmailsPage: Page } = await import("../features/admin/emails/emails-page");
  return { default: Page };
});

const AdminEmailCampaignsPage = lazy(async () => {
  const { CampaignsPage: Page } = await import(
    "../features/admin/emails/campaigns/campaigns-page"
  );
  return { default: Page };
});

const UnsubscribePage = lazy(async () => {
  const { UnsubscribePage: Page } = await import(
    "../features/email-preferences/unsubscribe-page"
  );
  return { default: Page };
});

const AdminIntegrationsPage = lazy(async () => {
  const { AdminIntegrationsPage: Page } = await import("../features/admin/settings-pages");
  return { default: Page };
});

const AdminTeamPage = lazy(async () => {
  const { AdminTeamPage: Page } = await import("../features/admin/team-page");
  return { default: Page };
});

const AdminWebhooksPage = lazy(async () => {
  const { AdminWebhooksPage: Page } = await import("../features/admin/settings-pages");
  return { default: Page };
});

function HealthPage() {
  return <h1>Interface disponível</h1>;
}

function AuthSessionBoundary() {
  return (
    <SessionProvider>
      <Outlet />
    </SessionProvider>
  );
}

export const appRoutes: RouteObject[] = [
  {
    element: <RouteBoundary />,
    errorElement: <AppRouteError />,
    children: [
      {
        path: "/",
        element: <StorefrontLayout />,
        children: [
          { index: true, element: <HomePage /> },
          { path: "/clube", element: <ClubPage /> },
          { path: "/sobre-mim", element: <AboutPage /> },
          {
            path: "/catalogo",
            element: <CatalogPage mode="catalog" />,
          },
          {
            path: "/busca",
            element: <CatalogPage mode="search" />,
          },
          {
            path: "/categoria/:slug",
            element: <CatalogPage mode="category" />,
          },
          {
            path: "/produto/:slug",
            element: <ProductPage />,
          },
          {
            path: "/carrinho",
            element: <Navigate replace to="/catalogo" />,
          },
        ],
      },
      {
        element: <CheckoutLayout />,
        children: [
          { path: "/checkout", element: <CheckoutPage /> },
          {
            path: "/pedido/:orderId/confirmacao",
            element: <ConfirmationPage />,
          },
          {
            path: "/pedido/:orderId/upsell",
            element: <UpsellPage />,
          },
        ],
      },
      {
        element: <AuthSessionBoundary />,
        children: [
          {
            element: <AuthLayout />,
            children: [
              { path: "/entrar", element: <LoginPage /> },
              {
                path: "/primeiro-acesso",
                element: <FirstAccessPage />,
              },
              {
                path: "/recuperar-senha",
                element: <RecoveryPage />,
              },
              {
                path: "/descadastrar",
                element: <UnsubscribePage />,
              },
              {
                path: "/redefinir-senha",
                element: <ResetPage />,
              },
            ],
          },
          {
            element: <MemberGuard />,
            children: [
              {
                path: "/minha-conta",
                element: <MembersLayout />,
                children: [
                  { index: true, element: <OverviewPage /> },
                  { path: "produtos", element: <LibraryPage /> },
                  { path: "clube", element: <ClubMemberPage /> },
                  {
                    path: "produtos/:productId",
                    element: <PurchasedProductPage />,
                  },
                  { path: "pedidos", element: <OrdersPage /> },
                  {
                    path: "pedidos/:orderId",
                    element: <OrderDetailPage />,
                  },
                  {
                    path: "assinaturas",
                    element: <SubscriptionsPage />,
                  },
                  { path: "pagamentos", element: <PaymentHistoryPage /> },
                  { path: "downloads", element: <DownloadHistoryPage /> },
                  { path: "perfil", element: <AccountPage /> },
                  { path: "seguranca", element: <AccountPage /> },
                  { path: "*", element: <Outlet /> },
                ],
              },
            ],
          },
          {
            element: <AdminGuard />,
            children: [
              {
                element: <AdminRouteGuard permission="checkout:editor" />,
                children: [
                  { path: "/admin/editor-checkout", element: <CheckoutStudioPage /> },
                ],
              },
              {
                path: "/admin",
                element: <AdminShell />,
                children: [
                  {
                    element: <AdminRouteGuard permission="dashboard:read" />,
                    children: [{ index: true, element: <AdminDashboardPage /> }],
                  },
                  {
                    element: <AdminRouteGuard permission="analytics:read" />,
                    children: [
                      { path: "desempenho", element: <AdminAnalyticsPage /> },
                      { path: "desempenho/conectar", element: <AdminAnalyticsConsentPage /> },
                    ],
                  },
                  {
                    element: <AdminRouteGuard permission="catalog:read" />,
                    children: [
                      {
                        path: "catalogo",
                        element: <Outlet />,
                        children: [
                          { index: true, element: <AdminCatalogListPage /> },
                          { path: ":productId/preview", element: <AdminProductPreviewPage /> },
                          {
                            element: <AdminRouteGuard permission="catalog:write" />,
                            children: [
                              // Same element as :productId so the editor survives the draft's first save.
                              { path: "novo", element: <AdminProductEditPage /> },
                              { path: "categorias", element: <AdminCategoryPage /> },
                              { path: ":productId", element: <AdminProductEditPage /> },
                            ],
                          },
                        ],
                      },
                    ],
                  },
                  {
                    element: <AdminRouteGuard permission="orders:read" />,
                    children: [
                      {
                        path: "pedidos",
                        element: <Outlet />,
                        children: [
                          { index: true, element: <AdminOrdersPage /> },
                          { path: ":orderId", element: <AdminOrderDetailPage /> },
                        ],
                      },
                    ],
                  },
                  {
                    element: <AdminRouteGuard permission="customers:read" />,
                    children: [
                      {
                        path: "clientes",
                        element: <Outlet />,
                        children: [
                          { index: true, element: <AdminCustomersPage /> },
                          { path: ":userId", element: <AdminCustomerDetailPage /> },
                        ],
                      },
                    ],
                  },
                  {
                    element: <AdminRouteGuard permission="subscriptions:read" />,
                    children: [{
                      path: "assinaturas",
                      element: <Outlet />,
                      children: [
                        { index: true, element: <AdminSubscriptionsPage /> },
                        { path: ":subscriptionId", element: <AdminSubscriptionDetailPage /> },
                      ],
                    }],
                  },
                  {
                    element: <AdminRouteGuard permission="catalog:write" />,
                    children: [{ path: "cupons", element: <AdminCouponsPage /> }],
                  },
                  {
                    element: <AdminRouteGuard permission="catalog:write" />,
                    children: [{ path: "order-bumps", element: <AdminOrderBumpsPage /> }],
                  },
                  {
                    element: <AdminRouteGuard permission="catalog:write" />,
                    children: [{ path: "upsells", element: <AdminUpsellsPage /> }],
                  },
                  {
                    element: <AdminRouteGuard permission="catalog:write" />,
                    children: [{ path: "pre-sells", element: <AdminPreSellsPage /> }],
                  },
                  {
                    element: <AdminRouteGuard permission="catalog:write" />,
                    children: [{ path: "cross-sells", element: <AdminCrossSellsPage /> }],
                  },
                  {
                    element: <AdminRouteGuard permission="home:editor" />,
                    children: [{ path: "editor-home", element: <HomeStudioPage /> }],
                  },
                  {
                    element: <AdminRouteGuard permission="settings:manage" />,
                    children: [
                      { path: "emails", element: <AdminEmailsPage /> },
                      { path: "emails/campanhas", element: <AdminEmailCampaignsPage /> },
                    ],
                  },
                  {
                    element: <AdminRouteGuard permission="settings:manage" />,
                    children: [{ path: "integracoes", element: <AdminIntegrationsPage /> }],
                  },
                  {
                    element: <AdminRouteGuard permission="webhooks:manage" />,
                    children: [{ path: "webhooks", element: <AdminWebhooksPage /> }],
                  },
                  {
                    element: <AdminRouteGuard permission="users:manage" />,
                    children: [{ path: "equipe", element: <AdminTeamPage /> }],
                  },
                  {
                    element: <AdminRouteGuard permission="audit:read" />,
                    children: [{ path: "auditoria", element: <AdminAuditPage /> }],
                  },
                ],
              },
            ],
          },
        ],
      },
      { path: "/health-ui", element: <HealthPage /> },
    ],
  },
];

export const router = createBrowserRouter(appRoutes);
